import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { Link } from '@/i18n/navigation';
import type { PlaylistContentData } from '@/lib/data/loaders/mongodb/playlists';
import { getOptionalCDNImageUrl } from '@/lib/utils/images';

interface PlaylistSidebarProps {
  playlistContent: PlaylistContentData;
}

const PlaylistSidebar = ({ playlistContent }: PlaylistSidebarProps) => {
  const t = useTranslations('common');

  // Extract data from playlistContent
  const creator = playlistContent.createdBy;
  const tags = playlistContent.tags || [];

  return (
    <div className="col-xl-3 col-md-4 offset-xl-1 position-relative fs-sm" data-testid="playlist-sidebar">
      <div className="position-sticky top-110px">
        {/* Curated By Panel */}
        {creator && (
          <div className="border rounded-5 p-4 mb-4" style={{ borderColor: '#777E90' }} data-testid="curated-by-panel">
            <h5 data-testid="curated-by-title">{t('global.commonTerms.curatedBy')}</h5>
            <a
              href={`https://open.spotify.com/user/${creator.spotify_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="d-flex align-items-center text-decoration-none fw-600"
              data-testid="curator-link"
            >
              {(() => {
                const creatorImageUrl = getOptionalCDNImageUrl(creator.image);
                return creatorImageUrl ? (
                  <div 
                    className="rounded-circle me-2 overflow-hidden flex-shrink-0"
                    style={{ width: 60, height: 60 }}
                  >
                    <Image
                      width={60}
                      height={60}
                      className="w-100 h-100"
                      style={{ objectFit: 'cover' }}
                      src={creatorImageUrl}
                      alt={creator.name}
                      data-testid="curator-image"
                    />
                  </div>
                ) : (
                  <span
                    className="rounded-circle me-2 bg-secondary d-flex justify-content-center align-items-center flex-shrink-0"
                    style={{ width: 60, height: 60 }}
                    data-testid="curator-placeholder"
                  >
                    <i className="bx bxs-user text-white"></i>
                  </span>
                );
              })()}
              <span className="text-white" data-testid="curator-name">
                {creator.name}
              </span>
            </a>
          </div>
        )}

        {/* Listen to Playlist Panel */}
        <div className="border rounded-5 p-4 mb-4" style={{ borderColor: '#777E90' }} data-testid="listen-now-panel">
          <h5 data-testid="listen-now-title">{t('playlists.pages.detail.components.hero.listenNow')}</h5>
          <div className="d-grid gap-2">
            <a
              href={playlistContent.spotifyPublicUrl || `https://open.spotify.com/playlist/${playlistContent.spotifyPlaylistId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-gradient d-inline-flex align-items-center rounded-5"
              data-testid="sidebar-spotify-button"
            >
              <i className="bx bxl-spotify fs-xl me-2" style={{ fontSize: '24px' }}></i>
              {t('global.actions.addToSpotify')}
            </a>
          </div>
        </div>

        {/* Related Tags */}
        {tags.length > 0 && (
          <div className="border rounded-5 p-4 mb-4" style={{ borderColor: '#777E90' }} data-testid="related-tags-panel">
            <h5 data-testid="related-tags-title">{t('playlists.sectionHeadings.relatedTags')}</h5>
            <div className="d-flex flex-wrap gap-1" data-testid="related-tags-list">
              {tags.slice(0, 8).map((tag) => (
                <Link
                  key={tag.slug}
                  href={`/playlists/tags/${tag.slug}`}
                  className="btn btn-sm btn-outline-secondary rounded-pill"
                  style={{ borderColor: '#353945' }}
                  data-testid={`related-tag-${tag.slug}`}
                >
                  #{tag.name}
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Follow Us Section */}
        <div className="border rounded-5 p-4 mb-4" style={{ borderColor: '#777E90' }} data-testid="follow-us-panel">
          <h5 data-testid="follow-us-title">{t('playlists.pages.detail.components.sidebar.followUs')}</h5>
          <ul className="d-flex align-items-center list-unstyled mx-0 mt-4" data-testid="social-links">
            <li>
              <a
                className="btn btn-icon btn-secondary bg-transparent border rounded-circle me-3"
                href={t('global.contact.spotifyProfileUrl')}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-spotify"
              >
                <i className="bx bxl-spotify" style={{ fontSize: '24px' }}></i>
              </a>
            </li>
            <li>
              <a
                className="btn btn-icon btn-secondary bg-transparent border rounded-circle me-3"
                href={`https://instagram.com/${t('global.contact.instaUsername')}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-instagram"
              >
                <i className="bx bxl-instagram" style={{ fontSize: '24px' }}></i>
              </a>
            </li>
            <li>
              <Link
                className="btn btn-icon btn-secondary bg-transparent border rounded-circle"
                href="/contact"
                data-testid="social-contact"
              >
                <i className="bx bx-envelope" style={{ fontSize: '24px' }}></i>
              </Link>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default PlaylistSidebar; 